import { useState } from 'react';
import { servicePriceRange, type Service } from '@acs/shared';
import { Banner, Button, Card, Icon, IconButton, Modal, Skeleton } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import type { AppointmentDoc, ServiceDoc } from '../../shared/api/converters.js';
import { formatTime } from '../../shared/lib/format.js';
import {
  useDeleteService,
  useServices,
  useUpcomingForService,
  useUpdateService,
  useUpsertService,
} from './hooks.js';
import type { ServiceEdit } from '../../shared/api/services.js';
import { useI18n } from '../../i18n/index.js';
import { ServiceDialog, useDurationLabel } from './ServiceDialog.js';

/** Which service the dialog is open for, if any (doc 64). `seq` remounts it
 *  fresh — draft and save error gone — every time it opens. */
type DialogState = { kind: 'closed' } | { kind: 'create'; seq: number } | { kind: 'edit'; seq: number; service: ServiceDoc };

/**
 * List / add / edit / remove the bookable services. Shared verbatim by the
 * Settings page and the setup wizard's services step — one editor, one
 * behaviour, and one dialog (doc 64) for adding and editing.
 *
 * The editor owns the two save mutations and the one dialog; rows only say
 * which service to edit or remove.
 *
 * `bare` drops the card: the wizard already frames its step in a panel, and a
 * card inside it drew a second border and a second set of paddings around the
 * same content.
 */
export function ServicesEditor({ businessId, title, bare }: { businessId: string; title?: string; bare?: boolean }) {
  const { t, tag } = useI18n();
  const { data, isLoading } = useServices(businessId);
  const upsert = useUpsertService(businessId);
  const update = useUpdateService(businessId);
  const remove = useDeleteService(businessId);
  const checkUpcoming = useUpcomingForService(businessId);
  const business = useActiveBusiness();
  /** The service whose Remove was refused, with the bookings that refused it. */
  const [blocked, setBlocked] = useState<{ service: ServiceDoc; bookings: AppointmentDoc[] } | null>(null);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [opened, setOpened] = useState(0);

  const services = data ?? [];
  const money = (value: number) =>
    new Intl.NumberFormat(tag, { style: 'currency', currency: business.currency }).format(value);
  const priceLabel = (s: ServiceDoc) => {
    const { from, to } = servicePriceRange(s);
    if (to !== null) return `${money(from)} – ${money(to)}`;
    return from ? money(from) : '';
  };
  const lengthLabel = useDurationLabel();

  const open = (next: { kind: 'create' } | { kind: 'edit'; service: ServiceDoc }) => {
    upsert.reset();
    update.reset();
    setOpened((n) => n + 1);
    setDialog({ ...next, seq: opened + 1 });
  };
  const close = () => setDialog({ kind: 'closed' });

  /**
   * Remove — unless the service still has bookings ahead (doc 45). Deleting it
   * would leave those appointments pointing at a service that no longer exists:
   * the assistant could not move them for the caller, and the owner would lose
   * the obvious way to see what is still owed. Checked at the moment of the
   * click, against the live book.
   */
  // `mutateAsync`, not `mutate(…, callbacks)`: per-call callbacks only fire for
  // the LATEST call, so a second row's click would swallow the first's answer.
  const requestRemove = async (service: ServiceDoc) => {
    setCheckingId(service.id);
    try {
      const bookings = await checkUpcoming.mutateAsync(service.id);
      if (bookings.length > 0) setBlocked({ service, bookings });
      else await remove.mutateAsync(service.id);
    } catch {
      /* shown by the banner below (isError) */
    } finally {
      setCheckingId((id) => (id === service.id ? null : id));
    }
  };

  /** Closes the dialog only once the write has succeeded; a failure keeps the
   *  draft open with its error. */
  const save = (edit: ServiceEdit) => {
    if (dialog.kind === 'edit') {
      update.mutate({ id: dialog.service.id, edit }, { onSuccess: close });
      return;
    }
    const { priceMax, ...rest } = edit;
    upsert.mutate(
      {
        service: {
          ...rest,
          // Absent, not null, for a fixed price: the schema has no null.
          ...(priceMax !== null ? { priceMax } : {}),
          active: true,
          sortOrder: services.length + 1,
        } satisfies Service,
      },
      { onSuccess: close },
    );
  };

  const detail = (s: ServiceDoc) =>
    [
      s.allDay ? t('services.allDay') : lengthLabel(s.durationMin),
      priceLabel(s),
      (s.questions?.length ?? 0) > 0 ? t('services.questionCount', { count: s.questions.length }) : '',
    ]
      .filter(Boolean)
      .join(' · ');

  const body = (
    <>
      <p className="field-hint services-editor__intro">{t('services.intro')}</p>

      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={52} radius="var(--tc-radius-sm)" />
          ))}
        </div>
      ) : services.length === 0 ? (
        <p className="muted">{t('services.empty')}</p>
      ) : (
        <ul className="service-list">
          {services.map((s) => (
            <ServiceRow
              key={s.id}
              service={s}
              detail={detail(s)}
              checking={checkingId === s.id}
              onEdit={() => open({ kind: 'edit', service: s })}
              onRemove={() => void requestRemove(s)}
            />
          ))}
        </ul>
      )}
      {(checkUpcoming.isError || remove.isError) && (
        <Banner tone="danger">{t('services.removeFailed')}</Banner>
      )}

      <div className="services-editor__add">
        <Button variant="secondary" leadingIcon={<Icon name="plus" size={16} />} onClick={() => open({ kind: 'create' })}>
          {t('services.addService')}
        </Button>
      </div>

      {dialog.kind !== 'closed' && (
        <ServiceDialog
          key={dialog.seq}
          service={dialog.kind === 'edit' ? dialog.service : null}
          pending={dialog.kind === 'edit' ? update.isPending : upsert.isPending}
          saveError={dialog.kind === 'edit' ? update.isError : upsert.isError}
          onSubmit={save}
          onClose={close}
        />
      )}

      {blocked && (
        <ServiceInUseDialog
          service={blocked.service}
          bookings={blocked.bookings}
          timezone={business.timezone}
          onClose={() => setBlocked(null)}
        />
      )}
    </>
  );

  return bare ? (
    <div className="services-editor">{body}</div>
  ) : (
    <Card title={title ?? t('services.title')}>{body}</Card>
  );
}

/** One service: its name and length · price · questions, and what can be done with it. */
function ServiceRow({
  service,
  detail,
  checking,
  onEdit,
  onRemove,
}: {
  service: ServiceDoc;
  detail: string;
  checking: boolean;
  onEdit(): void;
  onRemove(): void;
}) {
  const { t } = useI18n();
  return (
    <li className="service-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{service.name}</div>
        <div className="muted" style={{ fontSize: 13 }}>{detail}</div>
      </div>
      <IconButton
        size="sm"
        aria-label={t('services.edit', { name: service.name })}
        icon={<Icon name="edit" size={16} />}
        onClick={onEdit}
      />
      <IconButton
        size="sm"
        variant="danger"
        aria-label={t('services.remove', { name: service.name })}
        icon={<Icon name="trash" size={16} />}
        disabled={checking}
        onClick={onRemove}
      />
    </li>
  );
}

/** Why Remove did nothing: the bookings still ahead for this service (doc 45). */
function ServiceInUseDialog({
  service,
  bookings,
  timezone,
  onClose,
}: {
  service: ServiceDoc;
  bookings: AppointmentDoc[];
  timezone: string;
  onClose(): void;
}) {
  const { t, tag } = useI18n();
  const day = new Intl.DateTimeFormat(tag, { timeZone: timezone, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <Modal
      size="lg"
      closeLabel={t('ui.closeDialog')}
      title={t('services.inUseTitle', { name: service.name })}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      <p style={{ marginTop: 0 }}>{t('services.inUseDetail')}</p>
      <div className="service-in-use">
        <table>
          <thead>
            <tr>
              <th>{t('bookingForm.date')}</th>
              <th>{t('services.time')}</th>
              <th>{t('booking.customer')}</th>
              <th>{t('booking.phone')}</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id}>
                <td>{day.format(b.start)}</td>
                <td className="mono">
                  {formatTime(b.start, timezone, tag)}–{formatTime(b.end, timezone, tag)}
                </td>
                <td>{b.customerName}</td>
                <td className="mono">{b.customerPhone ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
